import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  signal
} from '@angular/core';
import { DesignFlatButton } from '@simlab/design/button';
import { ToastService } from '@simlab/design/toast';
import { PERSISTENT_FILE_CACHE } from '@simlab/simlab-facility-management/common';

const FILE_CACHE_CLEARED = $localize`:@@FILE_CACHE_CLEARED:Cache cleared`;

const MEGABYTE = 1024 * 1024;
const GIGABYTE = 1024 * MEGABYTE;

function formatBytes(bytes: number): string {
  const [value, unit] =
    bytes >= GIGABYTE ? [bytes / GIGABYTE, 'GB'] : [bytes / MEGABYTE, 'MB'];
  return `${Math.round(value * 10) / 10} ${unit}`;
}

/**
 * Personal settings card for the files (3D models, point clouds, splats)
 * the browser keeps on this device: shows the space they take and lets
 * the user remove them all.
 */
@Component({
  selector: 'simlab-file-cache',
  templateUrl: './file-cache.component.html',
  styleUrls: ['./file-cache.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DesignFlatButton],
  host: {
    class: 'simlab-file-cache'
  }
})
export class FileCacheComponent {
  private readonly _cache = inject(PERSISTENT_FILE_CACHE, { optional: true });
  private readonly _toastService = inject(ToastService);

  private readonly _usage = signal(0);
  protected readonly usage = computed(() => formatBytes(this._usage()));
  protected readonly clearing = signal(false);
  protected readonly available = !!this._cache;

  constructor() {
    void this._refreshUsage();
  }

  protected async clear(): Promise<void> {
    if (!this._cache || this.clearing()) return;
    this.clearing.set(true);
    try {
      await this._cache.clear();
      await this._refreshUsage();
      this._toastService.open(FILE_CACHE_CLEARED, 'Success');
    } finally {
      this.clearing.set(false);
    }
  }

  private async _refreshUsage(): Promise<void> {
    this._usage.set((await this._cache?.usage()) ?? 0);
  }
}
